import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Lightbulb, ChevronRight, Check, RotateCcw, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingHints = ({ page, onComplete }) => {
  const [hints, setHints] = useState([]);
  const [currentHint, setCurrentHint] = useState(null);
  const [hintsEnabled, setHintsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showAllHints, setShowAllHints] = useState(false);

  const fetchHints = useCallback(async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      if (!token) return;

      const res = await fetch(`${API_URL}/api/onboarding/hints?page=${page || ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) return;
      
      const data = await res.json();
      setHintsEnabled(data.hints_enabled);
      
      // Get first uncompleted hint for this page
      const uncompleted = (data.hints || []).filter(h => !h.completed);
      if (uncompleted.length > 0 && data.hints_enabled) {
        setCurrentHint(uncompleted[0]);
      }
      setHints(data.hints || []);
    } catch (error) {
      console.error('Error fetching hints:', error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchHints();
  }, [fetchHints]);

  const completeHint = async (hintId) => {
    try {
      const token = localStorage.getItem('movemate_token');
      await fetch(`${API_URL}/api/onboarding/hints/${hintId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setHints(prev => prev.map(h => 
        h.id === hintId ? { ...h, completed: true } : h
      ));

      // Move to next hint or close
      const remaining = hints.filter(h => !h.completed && h.id !== hintId);
      if (remaining.length > 0) {
        setCurrentHint(remaining[0]);
      } else {
        setCurrentHint(null);
      }

      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error completing hint:', error);
    }
  };

  const disableHints = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      await fetch(`${API_URL}/api/onboarding/disable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setHintsEnabled(false);
      setCurrentHint(null);
    } catch (error) {
      console.error('Error disabling hints:', error);
    }
  };

  const enableHints = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      await fetch(`${API_URL}/api/onboarding/enable`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setHintsEnabled(true);
      fetchHints();
    } catch (error) {
      console.error('Error enabling hints:', error);
    }
  };

  const resetOnboarding = async () => {
    try {
      const token = localStorage.getItem('movemate_token');
      await fetch(`${API_URL}/api/onboarding/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchHints();
    } catch (error) {
      console.error('Error resetting onboarding:', error);
    }
  };

  if (loading || !hintsEnabled || !currentHint) {
    return null;
  }

  // Position classes based on hint position
  const positionClasses = {
    'top': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    'left': 'top-1/2 left-4 -translate-y-1/2',
    'right': 'top-1/2 right-4 -translate-y-1/2',
  };

  return (
    <>
      {/* Current Hint Popup */}
      <div 
        className={`fixed z-50 ${positionClasses[currentHint.position] || 'bottom-4 right-4'}`}
        data-testid="onboarding-hint"
      >
        <Card className="w-80 shadow-2xl border-2 border-primary/20 animate-in slide-in-from-bottom-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary/10 rounded-lg flex-shrink-0">
                <Lightbulb className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">{currentHint.title}</h4>
                <p className="text-sm text-gray-600 mb-3">{currentHint.description}</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {hints.filter(h => h.completed).length + 1} of {hints.length}
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={disableHints}
                      className="text-xs text-gray-500"
                    >
                      <EyeOff className="h-3 w-3 mr-1" />
                      Hide Tips
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => completeHint(currentHint.id)}
                    >
                      Got it
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => setCurrentHint(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress indicator */}
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAllHints(!showAllHints)}
          className="bg-white shadow-lg"
        >
          <Lightbulb className="h-4 w-4 mr-2" />
          {hints.filter(h => h.completed).length}/{hints.length} Tips
        </Button>

        {showAllHints && (
          <Card className="absolute bottom-full mb-2 w-72 shadow-xl">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Getting Started</h4>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={resetOnboarding}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-2">
                {hints.map((hint) => (
                  <div 
                    key={hint.id}
                    className={`flex items-center gap-2 p-2 rounded ${
                      hint.completed ? 'bg-green-50' : 'bg-gray-50'
                    }`}
                  >
                    {hint.completed ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                    )}
                    <span className={`text-sm ${hint.completed ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                      {hint.title}
                    </span>
                  </div>
                ))}
              </div>
              {!hintsEnabled && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-3"
                  onClick={enableHints}
                >
                  Re-enable Tips
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default OnboardingHints;
